import { ArrowUpRight, ChevronsUpDown, FileCode2 } from "lucide-react";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { GITLAB_BASE_URL } from "../constants";
import { extractLines, useCodeSnippet } from "../hooks/useCodeSnippet";

const CONTEXT = { before: 4, after: 2 };
const WIDE_CONTEXT = { before: 15, after: 12 };

// Starts loading slightly before the snippet scrolls into view.
const useNearViewport = () => {
	const ref = useRef(null);
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		const element = ref.current;
		if (!element || visible) return;
		if (typeof IntersectionObserver === "undefined") {
			setVisible(true);
			return;
		}
		const observer = new IntersectionObserver(
			(entries) => {
				if (entries.some((entry) => entry.isIntersecting)) setVisible(true);
			},
			{ rootMargin: "300px" },
		);
		observer.observe(element);
		return () => observer.disconnect();
	}, [visible]);

	return [ref, visible];
};

const blobUrl = (project, code) =>
	`${GITLAB_BASE_URL}/${project}/-/blob/${encodeURIComponent(code.ref)}/${code.path
		.split("/")
		.map(encodeURIComponent)
		.join("/")}#L${code.line}`;

/**
 * The lines a diff comment is anchored to, syntax highlighted by GitLab, with
 * the commented line marked. Renders nothing for non-diff comments; shows only
 * the file reference while loading or when the code cannot be fetched.
 */
export const CodeSnippet = ({ project, code, compact = false }) => {
	const [ref, visible] = useNearViewport();
	const { file, error } = useCodeSnippet(project, code, visible);
	const [wide, setWide] = useState(false);
	const lines = useMemo(
		() =>
			file && code
				? extractLines(file, code.line, wide ? WIDE_CONTEXT : CONTEXT)
				: null,
		[file, code, wide],
	);
	if (!code) return null;

	const fileName = code.path.split("/").pop();
	const loading = !file && !error;
	const canWiden = !wide && lines?.length > 0;

	return (
		<figure
			ref={ref}
			className="code-snippet mt-2 min-w-0 overflow-hidden rounded-lg ring-1 ring-[var(--response-ring)]"
		>
			<figcaption className="flex min-w-0 items-center gap-1.5 bg-[var(--response-panel)] py-1 pl-3 pr-1 text-xs text-[var(--response-muted-text)]">
				<FileCode2 className="h-3.5 w-3.5 shrink-0" />
				<a
					href={project ? blobUrl(project, code) : undefined}
					target="_blank"
					rel="noopener noreferrer"
					title={`Open ${code.path}:${code.line} in GitLab`}
					className="group inline-flex min-w-0 items-center gap-1 hover:text-[var(--color-link)]"
				>
					<span className="truncate">
						<span className="font-medium text-[var(--color-text-primary)] group-hover:text-[var(--color-link)]">
							{fileName}
						</span>
						<span className="tabular-nums">:{code.line}</span>
						{fileName !== code.path && (
							<span className="ml-1.5">
								{code.path.slice(0, -fileName.length)}
							</span>
						)}
					</span>
					<ArrowUpRight className="h-3 w-3 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
				</a>
				{(canWiden || wide) && (
					<button
						type="button"
						onClick={() => setWide((value) => !value)}
						aria-pressed={wide}
						className="ml-auto inline-flex h-6 shrink-0 items-center gap-1 rounded-md px-1.5 hover:bg-[var(--response-bubble)] hover:text-[var(--color-text-primary)]"
					>
						<ChevronsUpDown className="h-3 w-3" />
						{wide ? "Less" : "More context"}
					</button>
				)}
			</figcaption>
			{loading && (
				<div
					className={`code-snippet-body animate-pulse ${compact ? "h-20" : "h-[7.5rem]"}`}
					aria-hidden
				/>
			)}
			{lines && lines.length > 0 && (
				<pre
					className={`code-snippet-body overflow-auto py-1 text-xs leading-5 ${wide ? "max-h-[28rem]" : ""}`}
				>
					<code>
						{lines.map(({ number, tokens, target }) => (
							<span
								key={number}
								className={`code-snippet-line flex ${target ? "code-snippet-target" : ""}`}
							>
								<span className="code-snippet-number w-10 shrink-0 select-none pr-3 text-right tabular-nums">
									{number}
								</span>
								<span className="whitespace-pre pr-3">
									{tokens.map((token, index) => (
										<span
											// biome-ignore lint/suspicious/noArrayIndexKey: tokens are positional and never reorder.
											key={index}
											className={token.className || undefined}
										>
											{token.text}
										</span>
									))}
									{tokens.length === 0 && " "}
								</span>
							</span>
						))}
					</code>
				</pre>
			)}
		</figure>
	);
};
